import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { saveAs } from 'file-saver';

import { sendRequest, isBeyondUnlocked, triggerEvent } from '../helpers/global.js';
import Storage from '../helpers/Storage.js';

import OverallRatingView from './charts/OverallRatingView';
import BreakdownView from './charts/BreakdownView';
import DashboardFilters from './common/DashboardFilters';
import FeedbackListView from './common/FeedbackListView';
import UnlockDialog from './common/UnlockDialog';

import '../sass/components/Dashboard.scss';

const mapStoreToProps = (store) => {
  return {
    user: store.data.user,
    venues: store.data.venues || [],
    isMobile: store.setup.isMobile,
  }
};

class FeedbackDashboard extends React.Component {
  constructor(props) {
    super(props);
    const filter = Storage.getData('dashboardFilter');
    this.state = {
      data: null,
      filter: {
        period: 'custom',
        from: '',
        to: '',
        ...filter,
      },
      dataRequested: false,
    };

    const beyondSelected = this.props.user.venues.filter(
      i => this.props.venues.includes(i.id) && i.beyond_partner
    );
    if (beyondSelected.length > 0) {
      Storage.setData('venues', beyondSelected.map(i => i.id));
    }
  }

  componentDidMount = () => {
    triggerEvent('logAmplitude', 'feedback - view page');
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (this.props.venues !== prevProps.venues && this.state.dataRequested) {
      this.requestData();
    }
  }

  requestData = () => {
    const beyond = isBeyondUnlocked(this.props.user, this.props.venues);
    if (!beyond) {
      return;
    }
    this.setState({dataRequested: true});
    sendRequest({
      method: 'dashboard/feedback',
      type: 'GET',
      data: {
        ...this.state.filter,
        venue_ids: this.props.venues,
      },
      success: (data) => {
        this.setState({data});
      },
      error: (data) => {
      }
    });
  }

  onDownloadCsv = () => {
    triggerEvent('logAmplitude', 'feedback - download CSV');
    sendRequest({
      method: 'dashboard/feedback_csv',
      type: 'GET',
      data: {
        ...this.state.filter,
        venue_ids: this.props.venues,
      },
      success: (data) => {
        const csvData = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        saveAs(csvData, 'feedback.csv');
      },
      error: (data) => {
      }
    });
  }

  renderPrivateRatings = (data) => {
    return (
      <div className='containterContent'>
        <div className='value'>{data.total}</div>
        <div className='name'>
          Private ratings
          <div className='info'>
            <div className='tooltip bottom'>
              Based on the private feedback that beyond customers give, after they redeem a deal
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderFirstTime = (data) => {
    return (
      <div className='containterContent'>
        <div className='value'>{data.first_time}</div>
        <div className='name'>
          First time customers
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': !this.props.isMobile,
              'center': this.props.isMobile,
            })}>
              Based on beyond’s customers’ response on whether they are here for the first time
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderLikelyVisit = (data) => {
    return (
      <div className='containterContent'>
        <div className='value'>{data.likely_revisit}</div>
        <div className='name'>
          Likely will revisit
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': !this.props.isMobile,
              'center': this.props.isMobile,
            })}>
              Based on beyond’s customers’ response on whether they are likely to revisit your venue again
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderOverall = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Overall rating
          <div className='info'>
            <div className={classnames('tooltip', {
              'center': this.props.isMobile,
            })}>
              Based on the private feedback that beyond customers give, after they redeem a deal
            </div>
          </div>
        </div>
        <div className='chart'>
          <OverallRatingView
            total={data.overall_rating.total}
            percent={data.overall_rating.percent_rating}
            breakdown={data.overall_rating_breakdown}
          />
        </div>
      </div>
    )
  }

  renderBreakdown = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Rating breakdown
          <div className='info'>
            <div className={classnames('tooltip', {
              'left': !this.props.isMobile,
              'center': this.props.isMobile,
            })}>
              Based on the private feedback that beyond customers give, broken 
              down by food, ambience and service
            </div>
          </div>
        </div>
        <div className='chart'>
          <BreakdownView data={data.rating_breakdown}/>
        </div>
      </div>
    )
  }

  renderFeedbacks = () => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Written feedback
          <div className='info'>
            <div className={classnames('tooltip', {
              'center': this.props.isMobile,
            })}>
              Other comments and feedback given by burpple beyond users. 
              Scale: Poor, Good, Superb
            </div>
          </div>
        </div>
        <FeedbackListView filter={this.state.filter}/>
      </div>
    )
  }

  renderLocked = () => {
    return (
      <div className='dashboard locked'>
        <div className='lockedPlaceholers'>
          <img className='graphPlaceholder big' src='/images/graph_placeholder_1.png' alt=''/>
          <img className='graphPlaceholder small' src='/images/graph_placeholder_2.png' alt=''/>
          <img className='graphPlaceholder small' src='/images/graph_placeholder_3.png' alt=''/>
        </div>
        <div className='lockedOverlay'/>
        <UnlockDialog/>
      </div>
    )
  }

  render = () => {
    const beyond = isBeyondUnlocked(this.props.user, this.props.venues);
    if (!beyond) {
      return this.renderLocked();
    }

    const data = this.state.data;
    return (
      <div className='dashboard'>
        <div className='dashboardInfo'>
          <div className='dashboardHeader'>
            <div className='dashboardTitle'>Customer feedback</div>
          </div>
          <div className='dashboardControls'>
            <DashboardFilters
              value={this.state.filter}
              onOpen={key => {
                if (key === 'interval') {
                  triggerEvent('logAmplitude', 'feedback - view monthly dropdown');
                } else if (key === 'period') {
                  triggerEvent('logAmplitude', 'feedback - view date filter');
                }
              }}
              onChange={change => {
                if (change.interval) {
                  triggerEvent('logAmplitude', 'feedback  - use monthly dropdown');
                } else if (change.period) {
                  triggerEvent('logAmplitude', 'feedback - use date filter');
                }
                const filter = { ...this.state.filter, ...change };
                Storage.setData('dashboardFilter', {
                  ...Storage.getData('dashboardFilter'),
                  ...filter,
                });
                this.setState({filter}, this.requestData);
              }}
              onDownloadCsv={this.onDownloadCsv}
            />
          </div>
        </div>
        <div className='dashboardContainer'>
          {data ?
            <div className='dashboardContent column3'>
              <div className='dashboardBlock small'>{this.renderPrivateRatings(data)}</div>
              <div className='dashboardBlock small'>{this.renderFirstTime(data)}</div>
              <div className='dashboardBlock small'>{this.renderLikelyVisit(data)}</div>
              <div className='dashboardBlock big'>{this.renderOverall(data)}</div>
              <div className='dashboardBlock small mobileFull'>{this.renderBreakdown(data)}</div>
              <div className='dashboardBlock huge'>{this.renderFeedbacks()}</div>
            </div>
          : null}
        </div>
      </div>
    )
  }
}

export default connect(mapStoreToProps)(FeedbackDashboard);
